import React, { useCallback, useEffect } from 'react';
import { useTournaments } from '@/hooks/useTournaments';
import TournamentCard from '../TournamentCard';
import Heading from '@/common/Heading';
import { useTournamentStore } from '@/lib/Tournaments/store';
import { twMerge } from 'tailwind-merge';
import { useRouter } from 'next/router';
import ArrayUtils from '@/utils/ArrayUtils';
import Message from '@/components/Message';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEmptySet } from '@fortawesome/pro-regular-svg-icons';

export type TournamentSection = 'upcoming' | 'live' | 'past';

interface ITournamentsSidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  onChoose?: (tournamentId: string) => void;
  sections?: TournamentSection[];
}

function TournamentsSidebar({ className, onChoose, sections }: ITournamentsSidebarProps) {
  const { currentTournamentId } = useTournamentStore();
  const router = useRouter();

  const handleChoose = useCallback(
    (id: string) => {
      router.push(`/tournaments/${id}`);
    },
    [router]
  );

  const { tournaments, isLoading } = useTournaments();
  const { liveTournaments, incomingTournaments, pastTournaments } = tournaments ?? {};

  const showSection = (s: TournamentSection) => !sections || sections.includes(s);

  useEffect(() => {
    if (onChoose) onChoose?.(currentTournamentId as string);
  }, [onChoose, currentTournamentId]);

  return (
    <aside className={twMerge('w-full pb-4 flex flex-col gap-6 scrollbar-apple lg:scrollbar-apple__stable-auto', className)}>
      {showSection('upcoming') && (
        <>
          <div className="flex flex-col gap-1">
            <Heading as="h4">
              <div className="flex items-center gap-2">
                Upcoming
                <span
                  className={twMerge(
                    'bg-success rounded-full size-2',
                    !ArrayUtils.isEmpty(incomingTournaments) && 'pulsing'
                  )}
                />
              </div>
            </Heading>
            {!ArrayUtils.isEmpty(incomingTournaments) ? (
              <div className="flex flex-col gap-4">
                {isLoading && <TournamentCard.Skeleton />}
                {incomingTournaments?.map((tournament) => (
                  <TournamentCard
                    key={tournament.id}
                    tournament={tournament}
                    onPress={handleChoose}
                    isCurrent={tournament.id === currentTournamentId}
                    className={`${currentTournamentId ? 'opacity-60' : ''}`}
                  />
                ))}
              </div>
            ) : (
              <Message
                condensed
                icon={<FontAwesomeIcon icon={faEmptySet} className="text-warning size-5" />}
                title="No upcoming tournaments yet!"
                subtitle="Check back soon 👀"
              />
            )}
          </div>
        </>
      )}

      {showSection('live') && !ArrayUtils.isEmpty(liveTournaments) && (
        <div className="flex flex-col gap-1">
          <Heading as="h4">
            <div className="flex items-center gap-2">
              Live
              <span className="pulsing bg-pink rounded-full size-2" />
            </div>
          </Heading>
          {!ArrayUtils.isEmpty(liveTournaments) ? (
            <div className="flex flex-col gap-4">
              {isLoading && <TournamentCard.Skeleton />}
              {liveTournaments?.map((tournament) => (
                <TournamentCard
                  key={tournament.id}
                  tournament={tournament}
                  onPress={handleChoose}
                  isCurrent={tournament.id === currentTournamentId}
                  className={`${currentTournamentId ? 'opacity-60' : ''}`}
                />
              ))}
            </div>
          ) : (
            <Message
              condensed
              icon={<FontAwesomeIcon icon={faEmptySet} className="text-warning size-5" />}
              title="No live tournaments at the moment"
              subtitle="Check back soon 👀"
            />
          )}
        </div>
      )}

      {showSection('past') && !ArrayUtils.isEmpty(pastTournaments) && (
        <div className="flex flex-col gap-1">
          <Heading as="h4">
            <div className="flex items-center gap-2">
              Past tournaments
              <span className="bg-secondary rounded-full size-2" />
            </div>
          </Heading>
          <div className="flex flex-col gap-4">
            {isLoading && <TournamentCard.Skeleton />}
            {pastTournaments?.map((tournament) => (
              <TournamentCard
                key={tournament.id}
                tournament={tournament}
                onPress={handleChoose}
                isCurrent={tournament.id === currentTournamentId}
                className={`${currentTournamentId ? 'opacity-60' : ''}`}
              />
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}

export default TournamentsSidebar;
